import React, { useEffect, useState } from 'react';
import { InsertDriveFile, Close, Download } from '@mui/icons-material';
import Button from '@/components/CustomButtons/Button';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { REFUND_FILE_TYPES, REFUND_STATUSES, openPdf } from '@/utility';
import {
  Modal,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Typography,
} from '@mui/material';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import { useConfirmation } from '@/context/ConfirmationContext';
import { useTranslation } from 'react-i18next';

const UPLOAD_TYPES = Object.freeze({
  INTERNAL: 'internal',
  PAYEE: 'payee',
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 900,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '90vh',
  overflow: 'auto',
};

const ManageFiles = ({
  refund,
  manualRefund,
  disabled = false,
  onChange,
  isPayeeAttachmentsEnabled,
  isInternalAttachmentsEnabled,
  isCustomPaymentTypesEnabled,
}) => {
  const { t } = useTranslation();
  const { requestConfirmation } = useConfirmation();
  const [attachments, setAttachments] = useState({
    internalAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
    payeeAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
  });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState(
    isInternalAttachmentsEnabled !== false
      ? UPLOAD_TYPES.INTERNAL
      : UPLOAD_TYPES.PAYEE,
  );
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!refund) return;

    const fetchFiles = async () => {
      const result = await axios.get(
        API_BASE_URL + `/refund-attachments/` + refund.id,
      );

      const files = result.data?.map((file) => ({
        originalFileName: file.original_file_name,
        awsKey: file.aws_key,
        uploadedBy: file.uploaded_by,
        uploadedAt: file.uploaded_at,
        fileType: file.type,
        local: false,
      }));

      // Preserve local files that are already in the state
      const existingLocalInternalFiles = attachments.internalAttachments.addFiles.filter(
        (file) => file.local === true,
      );
      const existingLocalPayeeFiles = attachments.payeeAttachments.addFiles.filter(
        (file) => file.local === true,
      );

      const newAttachments = {
        internalAttachments: {
          addFiles: [
            ...files.filter(
              (file) =>
                file.fileType !== REFUND_FILE_TYPES.PAYEE_FILE_ATTACHMENT,
            ),
            ...existingLocalInternalFiles,
          ],
          deletedFiles: attachments.internalAttachments.deletedFiles,
        },
        payeeAttachments: {
          addFiles: [
            ...files.filter(
              (file) =>
                file.fileType === REFUND_FILE_TYPES.PAYEE_FILE_ATTACHMENT,
            ),
            ...existingLocalPayeeFiles,
          ],
          deletedFiles: attachments.payeeAttachments.deletedFiles,
        },
      };

      setAttachments(newAttachments);
      if (onChange) {
        onChange(newAttachments);
      }
    };

    fetchFiles();
  }, [refund]);

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
    let defaultType = UPLOAD_TYPES.INTERNAL;
    if (
      (isInternalAttachmentsEnabled === false ||
        isCustomPaymentTypesEnabled !== true) &&
      isPayeeAttachmentsEnabled !== false
    ) {
      defaultType = UPLOAD_TYPES.PAYEE;
    } else if (
      isInternalAttachmentsEnabled !== false &&
      isCustomPaymentTypesEnabled === true &&
      isPayeeAttachmentsEnabled === false
    ) {
      defaultType = UPLOAD_TYPES.INTERNAL;
    }

    setUploadType(defaultType);
    setSelectedFiles([]);
    setIsDragOver(false);
  };

  const validateFileTypes = (files) => {
    const internalAllowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/tiff',
      'image/tif',
    ];
    const payeeAllowedTypes = ['application/pdf'];

    const allowedTypes =
      uploadType === UPLOAD_TYPES.PAYEE
        ? payeeAllowedTypes
        : internalAllowedTypes;

    const invalidTypeFiles = files.filter(
      (file) => !allowedTypes.includes(file.type),
    );

    if (invalidTypeFiles.length > 0) {
      const allowedExtensions =
        uploadType === UPLOAD_TYPES.PAYEE
          ? 'PDF'
          : 'PDF, DOCX, PNG, JPG, XLSX, TXT, TIF';
      return {
        valid: false,
        message: (
          <>
            {t('manageFiles.unsupportedFormats')}
            <br />
            <br />
            {invalidTypeFiles.map((f, i) => (
              <span key={i}>
                {f.name}
                <br />
              </span>
            ))}
            <br />
            {t('manageFiles.allowedFormats', { allowedExtensions })}
          </>
        ),
      };
    }

    return { valid: true };
  };

  const processSelectedFiles = (files) => {
    if (files.length > 20) {
      requestConfirmation(
        'Maximum 20 files allowed. Please select fewer files.',
        () => {},
      );
      return false;
    }

    const maxSize = 20 * 1024 * 1024;
    const invalidFiles = files.filter((file) => file.size > maxSize);

    if (invalidFiles.length > 0) {
      requestConfirmation(
        <>
          {t('manageFiles.filesExceed20MB')}
          <br />
          <br />
          {invalidFiles.map((f, i) => (
            <span key={i}>
              {f.name}
              <br />
            </span>
          ))}
          <br />
          {t('manageFiles.pleaseSelectFilesUnder20MB')}
        </>,
        () => {},
      );
      return false;
    }

    const fileTypeValidation = validateFileTypes(files);
    if (!fileTypeValidation.valid) {
      requestConfirmation(fileTypeValidation.message, () => {});
      return false;
    }

    setSelectedFiles(files);
    return true;
  };

  const handleFileSelection = (event) => {
    processSelectedFiles(Array.from(event.target.files));
    event.target.value = '';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    processSelectedFiles(Array.from(event.dataTransfer.files));
  };

  const handleUploadConfirm = () => {
    if (selectedFiles.length === 0) return;

    const fileTypeValidation = validateFileTypes(selectedFiles);
    if (!fileTypeValidation.valid) {
      requestConfirmation(fileTypeValidation.message, () => {});
      return;
    }

    const uploadedFiles = selectedFiles.map((file) => ({
      originalFileName: file.name,
      fileUrl: URL.createObjectURL(file),
      fileType: file.type,
      uploadedBy: 'You',
      file: file,
      local: true,
    }));

    const newAttachments = { ...attachments };

    if (uploadType === UPLOAD_TYPES.INTERNAL) {
      newAttachments.internalAttachments = {
        ...attachments.internalAttachments,
        addFiles: [
          ...attachments.internalAttachments.addFiles,
          ...uploadedFiles,
        ],
      };
    } else if (uploadType === UPLOAD_TYPES.PAYEE) {
      newAttachments.payeeAttachments = {
        ...attachments.payeeAttachments,
        addFiles: [...attachments.payeeAttachments.addFiles, ...uploadedFiles],
      };
    }

    setAttachments(newAttachments);
    if (onChange) {
      onChange(newAttachments);
    }

    setUploadModalOpen(false);
    setSelectedFiles([]);
  };

  const handleFileRemove = (index) => {
    const fileToRemove = attachments.internalAttachments.addFiles[index];

    if (fileToRemove.local === true) {
      const newAttachments = {
        ...attachments,
        internalAttachments: {
          ...attachments.internalAttachments,
          addFiles: attachments.internalAttachments.addFiles.filter(
            (_, i) => i !== index,
          ),
        },
      };
      setAttachments(newAttachments);
      if (onChange) {
        onChange(newAttachments);
      }
      return;
    }

    const newDeleted = [
      ...attachments.internalAttachments.deletedFiles,
      fileToRemove.awsKey,
    ];
    const deletedSet = new Set(newDeleted);

    const newAttachments = {
      ...attachments,
      internalAttachments: {
        ...attachments.internalAttachments,
        deletedFiles: [...deletedSet],
      },
    };

    setAttachments(newAttachments);
    if (onChange) {
      onChange(newAttachments);
    }
  };

  const handleDownload = async (file, e) => {
    e.stopPropagation();
    const fileUrl =
      file.fileUrl ??
      API_BASE_URL +
        '/getFile_facility?filePath=' +
        file.awsKey +
        '&view=inline';

    try {
      const response = await axios.get(fileUrl, {
        responseType: 'blob',
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderFileCard = (file, index, isInternal, handleRemove) => {
    const bgColor = isInternal ? '#36454F' : '#2d5f3f';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
        key={`${isInternal ? 'internal' : 'payee'}-${index}`}>
        <div
          title={
            'Type: ' +
            (isInternal ? 'Internal' : 'Payee') +
            '\n' +
            'File Name: ' +
            file.originalFileName +
            '\n' +
            ('Uploaded By: ' + file.uploadedBy)
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px',
            background: bgColor,
            borderRadius: '5px',
            fontSize: '14px',
            color: 'white',
            position: 'relative',
            cursor: 'pointer',
            minWidth: '180px',
            flex: 1,
          }}
          onClick={() =>
            openPdf(
              file.fileUrl ??
                API_BASE_URL +
                  '/getFile_facility?filePath=' +
                  file.awsKey +
                  '&view=inline',
            )
          }>
          <InsertDriveFile style={{ color: '#3ab895', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {file.originalFileName.length > 25
              ? `${file.originalFileName.substring(0, 25)}...`
              : file.originalFileName}
          </div>
          {(refund?.refund_status === REFUND_STATUSES.EXCEPTION ||
            refund?.refund_status === REFUND_STATUSES.UNCLAIMED ||
            manualRefund) &&
            !disabled && (
              <div
                title=''
                style={{
                  background: '#e91e63',
                  zIndex: 10,
                  position: 'absolute',
                  top: '-10px',
                  left: '-10px',
                  cursor: 'pointer',
                  height: '22px',
                  width: '22px',
                  borderRadius: '50%',
                  padding: '2px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(index);
                }}>
                <Close
                  color='secondary'
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              </div>
            )}
        </div>
        {!file.local && (
          <div
            title={t('manageFiles.downloadFile')}
            style={{
              background: '#3ab895',
              cursor: 'pointer',
              height: '40px',
              width: '40px',
              borderRadius: '25%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onClick={(e) => handleDownload(file, e)}>
            <Download style={{ color: 'white' }} />
          </div>
        )}
      </div>
    );
  };

  const internalFiles = attachments.internalAttachments.addFiles;
  const internalDeletedFiles = attachments.internalAttachments.deletedFiles;
  const payeeFiles = attachments.payeeAttachments.addFiles;
  const payeeDeletedFiles = attachments.payeeAttachments.deletedFiles;

  const visibleInternalFiles = internalFiles.filter(
    (file) => !internalDeletedFiles.includes(file.awsKey) || file.local,
  );
  const visiblePayeeFiles = payeeFiles.filter(
    (file) => !payeeDeletedFiles.includes(file.awsKey) || file.local,
  );

  return (
    <div
      style={{
        marginTop: '20px',
        padding: '10px 0',
      }}>
      {visibleInternalFiles.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <Typography
            variant='h6'
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#3ab895',
            }}>
            {t('manageFiles.internalAttachments')}
          </Typography>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px',
            }}>
            {internalFiles.map((file, index) => {
              return (
                (!internalDeletedFiles.includes(file.awsKey) || file.local) &&
                renderFileCard(file, index, true, handleFileRemove)
              );
            })}
          </div>
        </div>
      )}

      {visiblePayeeFiles.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <Typography
            variant='h6'
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#3ab895',
            }}>
            {t('manageFiles.payeeAttachments')}
          </Typography>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px',
            }}>
            {payeeFiles.map((file, index) => {
              return (
                (!payeeDeletedFiles.includes(file.awsKey) || file.local) &&
                renderFileCard(file, index, false, (idx) => {
                  const fileToRemove =
                    attachments.payeeAttachments.addFiles[idx];

                  if (fileToRemove.local === true) {
                    const newAttachments = {
                      ...attachments,
                      payeeAttachments: {
                        ...attachments.payeeAttachments,
                        addFiles: attachments.payeeAttachments.addFiles.filter(
                          (_, i) => i !== idx,
                        ),
                      },
                    };
                    setAttachments(newAttachments);
                    if (onChange) {
                      onChange(newAttachments);
                    }
                    return;
                  }

                  const newDeleted = [
                    ...attachments.payeeAttachments.deletedFiles,
                    fileToRemove.awsKey,
                  ];
                  const deletedSet = new Set(newDeleted);

                  const newAttachments = {
                    ...attachments,
                    payeeAttachments: {
                      ...attachments.payeeAttachments,
                      deletedFiles: [...deletedSet],
                    },
                  };

                  setAttachments(newAttachments);
                  if (onChange) {
                    onChange(newAttachments);
                  }
                })
              );
            })}
          </div>
        </div>
      )}

      {(refund?.refund_status === REFUND_STATUSES.EXCEPTION ||
        refund?.refund_status === REFUND_STATUSES.UNCLAIMED ||
        manualRefund) &&
        !disabled && (
          <div
            style={{
              marginTop: '20px',
            }}>
            <Button color='primary' onClick={handleOpenUploadModal}>
              {t('manageFiles.uploadFiles')}
            </Button>
          </div>
        )}

      <Modal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
        <Box sx={modalStyle}>
          <Typography variant='h6' component='h2' gutterBottom>
            {t('manageFiles.uploadFiles')}
          </Typography>

          <GridContainer>
            {isCustomPaymentTypesEnabled === true && (
              <GridItem xs={12} sm={12} md={6}>
                <div
                  style={{
                    border: '1px dashed #ccc',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    marginTop: '10px',
                    backgroundColor:
                      uploadType === UPLOAD_TYPES.INTERNAL
                        ? 'transparent'
                        : '#f5f5f5',
                  }}>
                  <FormControl component='fieldset'>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={uploadType === UPLOAD_TYPES.INTERNAL}
                          onChange={() => setUploadType(UPLOAD_TYPES.INTERNAL)}
                          name='internalAttachments'
                          disabled={isInternalAttachmentsEnabled === false}
                        />
                      }
                      label={
                        <>
                          <Typography
                            variant='body1'
                            style={{ fontWeight: 'bold' }}>
                            {t('manageFiles.internalAttachments')}
                          </Typography>
                          <Typography variant='body2' style={{ color: '#666' }}>
                            {t('manageFiles.internalAttachmentsDescription')}
                          </Typography>
                        </>
                      }
                    />
                  </FormControl>
                </div>
              </GridItem>
            )}

            <GridItem
              xs={12}
              sm={12}
              md={isCustomPaymentTypesEnabled === true ? 6 : 12}>
              <div
                style={{
                  border: '1px dashed #ccc',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  marginTop: '10px',
                  backgroundColor:
                    uploadType === UPLOAD_TYPES.PAYEE
                      ? 'transparent'
                      : '#f5f5f5',
                }}>
                <FormControl component='fieldset'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={uploadType === UPLOAD_TYPES.PAYEE}
                        onChange={() => setUploadType(UPLOAD_TYPES.PAYEE)}
                        name='payeeAttachments'
                        disabled={isPayeeAttachmentsEnabled === false}
                      />
                    }
                    label={
                      <>
                        <Typography
                          variant='body1'
                          style={{ fontWeight: 'bold' }}>
                          {t('manageFiles.payeeAttachments')}
                        </Typography>
                        <Typography variant='body2' style={{ color: '#666' }}>
                          {t('manageFiles.payeeAttachmentsDescription')}
                        </Typography>
                      </>
                    }
                  />
                </FormControl>
              </div>
            </GridItem>
          </GridContainer>

          <div
            style={{
              marginTop: '20px',
              padding: '32px',
              border: isDragOver ? '2px dashed #3ab895' : '2px dashed #ccc',
              borderRadius: '8px',
              textAlign: 'center',
              minHeight: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: isDragOver ? '#f0faf7' : 'transparent',
            }}
            onClick={() => document.getElementById('modal-file-upload').click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            <input
              type='file'
              accept={
                uploadType === UPLOAD_TYPES.PAYEE
                  ? 'application/pdf'
                  : '.pdf,.docx,.png,.jpg,.jpeg,.xlsx,.txt,.tif,.tiff'
              }
              multiple
              style={{ display: 'none' }}
              id='modal-file-upload'
              onChange={handleFileSelection}
            />
            {selectedFiles.length === 0 ? (
              <Typography variant='body1' style={{ color: '#666' }}>
                {t('manageFiles.clickHereToSelectFilesOrDragAndDrop')}
              </Typography>
            ) : (
              <div>
                <Typography variant='body2' style={{ fontWeight: 'bold' }}>
                  {t('manageFiles.filesSelected', {
                    count: selectedFiles.length,
                  })}
                </Typography>
                {selectedFiles.map((file, index) => (
                  <Typography
                    key={index}
                    variant='body2'
                    style={{ color: '#666', marginTop: '4px' }}>
                    {file.name}
                  </Typography>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              marginTop: '24px',
            }}>
            <Button onClick={() => setUploadModalOpen(false)}>
              {t('manageFiles.cancel')}
            </Button>
            <Button
              color='rose'
              onClick={handleUploadConfirm}
              disabled={selectedFiles.length === 0 || !uploadType}>
              {t('manageFiles.done')}
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default ManageFiles;
